/* eslint-disable ember/no-controller-access-in-routes, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import {
  loadTemplateWithSections,
  templateDisplayName,
} from 'linced-app/utils/reporting';

export default class WriteRoute extends Route {
  // Services
  @service
  dates;

  @service
  session;

  @service
  store;

  async model(params) {
    let { person } = this.modelFor('reporting.group.student');

    let studentReport =
      this.store.peekRecord(
        'reports/student-report',
        params.student_report_id,
      ) ||
      (await this.store.findRecord(
        'reports/student-report',
        params.student_report_id,
      ));

    let templateId = studentReport.belongsTo('reportTemplateId').id();
    let template = await loadTemplateWithSections(this.store, templateId);

    // Load student-report-sections
    if (studentReport) {
      await this.store.query('reports/student-report-section', {
        studentReportId: studentReport.get('id'),
      });
    }

    // Get content sections
    let sections = this.store
      .peekAll('reports/report-template-section')
      .filter(
        (section) =>
          section.belongsTo('reportTemplateId').id() === template.get('id') &&
          section.sectionType === ReportTemplateSectionType.CONTENT,
      )
      .sort((a, b) => a.sequence - b.sequence);

    // Preload subjects referenced by content sections
    let subjectIds = new Set();
    sections.forEach((section) => {
      let config = section.get('configuration');
      if (!config) return;
      try {
        let parsed = typeof config === 'string' ? JSON.parse(config) : config;
        if (parsed.subjectId) {
          subjectIds.add(parsed.subjectId);
        }
      } catch (err) {
        // ignore
      }
    });

    await Promise.all(
      Array.from(subjectIds).map((id) =>
        this.store.findRecord('goals/subject', id).catch(() => null),
      ),
    );

    await Promise.all([
      this.store.findAll('milestones/expectation').catch(() => null),
      this.store.findAll('milestones/level').catch(() => null),
    ]);

    return { template, sections, person, studentReport };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let groupCtrl = this.controllerFor('reporting.group');
    groupCtrl.set('isWriteRoute', true);
    groupCtrl.set('activeReport', model.template);
    // Name the template for the report actually being edited, from that
    // report's own year and period. Rebuilt here for the same reason the group
    // write/review/view routes rebuild it: displayName lives on the shared
    // template record, so whichever screen touched it last would otherwise
    // win and the breadcrumb would name a different period. A retired template
    // never gets one set at all (reporting.js only names active templates),
    // which left the breadcrumb blank.
    // year defaults to the Go zero value 0, so 0 means "not set".
    model.template.set(
      'displayName',
      templateDisplayName(
        model.template,
        model.studentReport.periodIndex,
        model.studentReport.year || this.dates.currentAcademicYear,
      ),
    );

    let studentCtrl = this.controllerFor('reporting.group.student');
    studentCtrl.set('isStudentWriteRoute', true);

    controller.initSingleStudentWriteView(
      model.template,
      model.sections,
      model.person,
      model.studentReport,
    );
  }

  // `renderTemplate`/`render` were removed in Ember 4.0. `templateName` is
  // the supported way to choose a template; a getter keeps it dynamic.
  get templateName() {
    return isMobile()
      ? 'reporting/group/student/write-mobile'
      : 'reporting/group/student/write';
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      this.controllerFor('reporting.group').set('isWriteRoute', false);
      this.controllerFor('reporting.group').set('activeReport', null);
      this.controllerFor('reporting.group.student').set(
        'isStudentWriteRoute',
        false,
      );
      controller.cleanupWriteView();
    }
  }
}
